/**
 * Block 13 PR-G Phase 3 — Block 12 → Block 13 fromQuote deeplink mapping.
 *
 * Pure function (no React, no I/O) — maps a saved Block 12 quote into a
 * partial Block 13 contract draft. Called from `useFromQuoteSeeding` после
 * `loadSavedQuote` resolves; consumer (Phase 4 ContractForm) merges this seed
 * with form defaults и validates через `ContractDraftSchema` before save.
 *
 * Mapping per ADR-0025 §Q7:
 *   - quote.state.furnitureType → spec[0].title
 *   - quote.state.compositionRows[] → spec[0].rows[] (each:
 *     {id: genUuid(), source: 'fromQuote', name, description: itemType + brand, cost: 0})
 *   - quote.state.costWithMargin → totalCost + costSource = 'fromQuote'
 *   - quote.state.comments → contract.comments
 *   - quote.id → contract.fromQuoteId
 *
 * **Phase 6.5 #10 — conditions mapping extension.** quote.state.conditions[]
 * пробрасывает стандартные KP-условия в contract:
 *   - row.id='production-time'.value → durationDays (clamp 1..365)
 *   - row.id='advance'.value → advance (clamp 0..1e11)
 *   - row.id='warranty'.value → warrantyTerm (clamp 1..120) +
 *     warrantyUnit='months' (KP always месяцы)
 * Empty / non-numeric / out-of-range value → skipped (defaults preserved).
 *
 * Fallback (compositionRows empty): single spec row {name: furnitureType,
 * cost: costWithMargin, source: 'fromQuote'} — Phase 4 UI per-row override
 * (chip-segment «Из списка / Вручную / Из КП») доставит детали.
 *
 * Defensive: если furnitureType null AND compositionRows empty → omit spec
 * entirely (Phase 4 UI создаст блок при first add). Zod
 * `ContractSpecBlock.title.min(1)` валидирует — мы не возвращаем пустые блоки.
 *
 * Reference:
 *   - ADR-0025 §Q7 (canonical mapping)
 *   - packages/shared/src/contracts/contracts.ts (ContractDraft schema)
 *   - apps/web/src/features/calculator/quote/state/savedQuotesIdb.ts (SavedQuote)
 *   - apps/web/src/features/calculator/quote/conditionRows.ts — stable IDs
 *     (production-time / advance / warranty) для КП → Договор mapping.
 */

import { resolveAdvanceAmount } from '@pomoshnik/shared';
import type { ContractDraft, ContractSpecBlock } from '@pomoshnik/shared';

import type { SavedQuote } from '@/features/calculator/quote/state/savedQuotesIdb';
import type { ConditionRow } from '@/features/calculator/quote/types';
import { isCompositionRowEmpty } from '@/features/calculator/quote/compositionRowUtils';
import { genUuid } from '@/lib/uuid';

import { applySpecBlockSeed, buildDefaultSpecRows } from '../state/contractDraftDefaults';
import { computeSpecHash } from '../state/contractSpecHash';
import type { SpecBlockSeedCustomRow, SpecBlockSeedValues } from '../state/contractReducer';

/**
 * Phase 6.5F Amendment A7 (2026-05-25) — type/brand max alignment с new
 * ContractSpecRowSchema (max 500 each). `VALUE_MAX = 2000` removed
 * (`value` field DROPPED в A7). type + brand передаются 1:1 от КП
 * CompositionRow без concat.
 */
const TYPE_MAX = 500;
const BRAND_MAX = 500;

function clamp(s: string, max: number): string {
  return s.length > max ? s.slice(0, max) : s;
}

// Phase 6.5 #10 — stable IDs из conditionRows.ts (Block 12). Литералы используются
// напрямую (не импорт constants), потому что conditionRows.ts экспортирует
// только ADVANCE_ROW_ID + REMAINDER_ROW_ID — остальные inline в DEFAULT_CONDITIONS.
// Изменение этого списка требует синхронизации с conditionRows.ts.
const KP_PRODUCTION_TIME_ID = 'production-time';
const KP_ADVANCE_ID = 'advance';
const KP_WARRANTY_ID = 'warranty';

// Bounds (mirror ContractDraftSchema field caps).
const DURATION_DAYS_MIN = 1;
const DURATION_DAYS_MAX = 365;
const ADVANCE_MIN = 0;
const ADVANCE_MAX = 100_000_000_000;
const WARRANTY_TERM_MIN = 1;
const WARRANTY_TERM_MAX = 120;

/**
 * Parses ConditionRow value string → integer if valid, finite, in [min, max].
 * Returns null если empty / non-numeric / out-of-range — caller skips field.
 */
function parseClampedInt(raw: string, min: number, max: number): number | null {
  if (raw.trim() === '') return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  const i = Math.trunc(n);
  if (i < min || i > max) return null;
  return i;
}

/**
 * Phase 6.5F Amendment A7 (2026-05-25) — extract single field (itemType OR
 * brand) от КП row, normalizing '—' placeholder (Block 12 convention для
 * unfilled cells) к undefined. Replaces 6.5B `buildValue` which concatenated
 * itemType + brand в single string — теперь type/brand передаются 1:1
 * separately.
 */
function extractField(value: string): string | undefined {
  const trimmed = value.trim();
  if (trimmed === '' || trimmed === '—') return undefined;
  return trimmed;
}

/**
 * Phase 6.5C — composite seed shape для ADD_SPEC_BLOCK dispatch + initial
 * fromQuote seeding. Pure data; consumed reducer'ом + `applySpecBlockSeed`
 * helper'ом.
 */
export interface SpecBlockSeedFromQuote {
  title: string;
  seedValues: SpecBlockSeedValues;
  customRows: SpecBlockSeedCustomRow[];
}

/**
 * Phase 6.5F Amendment A7 (2026-05-25) — 1:1 direct copy от КП.
 *
 * **Before (6.5D Item #3):** heuristic distribution — `facades` → `facade_material.value`,
 * `fittings` → `hardware.value`, rest → custom rows с combined value.
 *
 * **After (6.5F A7):** NO heuristic. ВСЕ filled КП composition rows → contract
 * custom rows (removable=true) с 1:1 field copy:
 *   - row.label = КП row.name («Столешница», «Фасады», «Фурнитура»)
 *   - row.type = КП row.itemType («ЛДСП», «Пленка», «Blum»)
 *   - row.brand = КП row.brand («Эггер», «Кира»)
 *   - row.cost = undefined (Block 12 не хранит per-row cost)
 *
 * **Block-level:**
 *   - `block_cost.cost` = `state.costWithMargin ?? 0` (общий cost quote'а)
 *   - title = state.furnitureType?.trim() либо fallback «Без названия»
 *
 * **Filter (PR-C3, ADR-0037):** «все видимые в КП» позиции 1:1 — используем тот
 * же предикат видимости, что КП UI/PDF — `!isCompositionRowEmpty(r)`. Включает
 * кастомные строки (всегда) + seeded default-строки + default с Тип/Бренд;
 * исключает только пустые default-категории. Позиции с заполненным названием
 * но без Тип/Бренд (например пользовательские «Ручки») ТЕПЕРЬ переносятся
 * (label есть, type/brand → undefined). До PR-C3 фильтр был `itemType||brand`
 * (строже), и такие позиции отбрасывались.
 *
 * **Rationale (A7):** combined value field теряло Тип/Бренд discrimination.
 * New schema mirrors КП structure → mapping становится тривиальным.
 */
export function buildSpecBlockSeedFromQuote(quote: SavedQuote): SpecBlockSeedFromQuote {
  const composition = quote.state.compositionRows ?? [];
  // PR-C3 (ADR-0037) — единый предикат видимости КП (A.4): то же, что
  // SectionComposition.tsx + renderQuoteToPdf.ts. «Видно в КП = есть в Договоре».
  const filled = composition.filter((r) => !isCompositionRowEmpty(r));

  const trimmedFurnitureType = quote.state.furnitureType?.trim() ?? '';
  const title = trimmedFurnitureType !== '' ? trimmedFurnitureType : 'Без названия';

  const blockCost = quote.state.costWithMargin ?? 0;
  const seedValues: SpecBlockSeedValues = { block_cost: blockCost };

  // Phase 6.5F A7 — 1:1 copy без heuristic. Каждая filled КП composition row →
  // contract custom row с separate type/brand fields.
  const customRows: SpecBlockSeedCustomRow[] = filled.map((r) => {
    const type = extractField(r.itemType);
    const brand = extractField(r.brand);
    return {
      id: genUuid(),
      label: r.name,
      type: type !== undefined ? clamp(type, TYPE_MAX) : undefined,
      brand: brand !== undefined ? clamp(brand, BRAND_MAX) : undefined,
      // cost остаётся undefined — Block 12 не хранит per-row cost; user заполнит в UI.
      cost: undefined,
    };
  });

  return { title, seedValues, customRows };
}

/**
 * Phase 6.5 #10 — extract relevant condition values из KP в contract fields.
 * Mutates `target` (Partial<ContractDraft>) только для тех полей, у которых
 * KP содержит валидное значение. Defaults в ContractDraft schema сохраняются
 * если KP value empty / invalid.
 */
function applyConditionsToDraft(
  conditions: readonly ConditionRow[],
  target: Partial<ContractDraft>,
): void {
  for (const row of conditions) {
    if (row.id === KP_PRODUCTION_TIME_ID) {
      const v = parseClampedInt(row.value, DURATION_DAYS_MIN, DURATION_DAYS_MAX);
      if (v !== null) target.durationDays = v;
    } else if (row.id === KP_ADVANCE_ID) {
      const v = parseClampedInt(row.value, ADVANCE_MIN, ADVANCE_MAX);
      if (v !== null) target.advance = v;
    } else if (row.id === KP_WARRANTY_ID) {
      const v = parseClampedInt(row.value, WARRANTY_TERM_MIN, WARRANTY_TERM_MAX);
      if (v !== null) {
        target.warrantyTerm = v;
        target.warrantyUnit = 'months';
      }
    }
  }
}

export function mapQuoteStateToContractDraft(quote: SavedQuote): Partial<ContractDraft> {
  const state = quote.state;
  const result: Partial<ContractDraft> = {
    costSource: 'fromQuote',
    totalCost: state.costWithMargin ?? 0,
    comments: state.comments,
    fromQuoteId: quote.id,
    // PR-C2 Phase 2 (ADR-0036) — bookkeeping для invalidation/частичного пересева.
    // `quote.updatedAt` — триггер invalidation на forward-открытии Договора.
    // `seededFromQuoteAt` (wall-clock) добавляют call-sites (hook/container),
    // чтобы mapping остался детерминированным (pure) для unit-тестов.
    lastQuoteUpdatedAt: quote.updatedAt,
  };

  // Phase 6.5 #10 — extract KP conditions (advance, durationDays, warrantyTerm).
  applyConditionsToDraft(state.conditions, result);

  // PR-D2 (ADR-0040) — если КП в %-режиме («Аванс, %» задан), строка-условие
  // «Размер аванса» хранит ручной fallback (пустой/устаревший). Договор должен
  // получить ЭФФЕКТИВНУЮ сумму-снимок = round(Стоимость × %/100). В ручном
  // режиме КП (advancePercent null/undefined) — НЕ трогаем result.advance,
  // чтобы сохранить прежнее поведение (advance undefined при пустой строке).
  // Ставку % в Договор НЕ переносим (договор стартует в ручном режиме —
  // минимальный blast radius, ADR-0040 §5).
  if (state.advancePercent != null && Number.isFinite(state.advancePercent)) {
    result.advance = resolveAdvanceAmount(
      state.costWithMargin ?? 0,
      state.advancePercent,
      result.advance ?? 0,
    );
  }

  const trimmedFurnitureType = state.furnitureType?.trim() ?? '';
  const hasFurnitureType = trimmedFurnitureType !== '';
  const hasComposition = (state.compositionRows ?? []).length > 0;

  if (!hasFurnitureType && !hasComposition) {
    // Both пусты — omit spec (payload без spec). HYDRATE НЕ перепишет spec, и для
    // fromQuote-входа ('auto' entryMode) фактически сохранится initial single
    // block_cost блок (title ''), а НЕ пустой массив. Поэтому seededSpecHash
    // считаем от этого post-hydrate shape, иначе pristine-проверка при будущем
    // пересеве ложно классифицирует нетронутый договор как dirty (reviewer
    // PR-C2 medium). Хеш исключает block_cost+id → canonical [{title:'',rows:[]}].
    result.seededSpecHash = computeSpecHash([{ id: '', title: '', rows: buildDefaultSpecRows() }]);
    return result;
  }

  // Phase 6.5C polish — use shared `buildSpecBlockSeedFromQuote` helper:
  //   - filters empty/«—» positions per Aibek decision #12
  //   - distributes first 3 filled positions → facade_material/corpus_material/hardware
  //   - 4th+ filled positions → custom rows
  //   - block_cost.cost = state.costWithMargin ?? 0
  //   - UUID consolidation (1 UUID per custom row)
  // `applySpecBlockSeed` then materializes seed onto default rows + appends customs.
  const seed = buildSpecBlockSeedFromQuote(quote);
  const block: ContractSpecBlock = {
    id: genUuid(),
    title: seed.title,
    rows: applySpecBlockSeed(buildDefaultSpecRows(), seed.seedValues, seed.customRows),
    // PR-C5 (ADR-0039) — маркер родного блока: адресует пересев из КП.
    sourceQuoteId: quote.id,
  };
  result.spec = [block];
  // PR-C2 Phase 2 (ADR-0036, механизм B) — отпечаток КП-производной спецификации
  // на момент сева. Pristine-детектор при будущем пересеве (см. computeSpecHash).
  result.seededSpecHash = computeSpecHash(result.spec);
  return result;
}
